import { useEffect, useState } from "react";
import Shell from "../../components/Shell";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Plus } from "lucide-react";

const CATEGORIES = ["ifu", "brochure", "protocol", "consent", "marketing"];

export default function AdminDownloads() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [brands, setBrands] = useState([]);

    const reload = async () => {
        const [d, b] = await Promise.all([api.get("/admin/downloads"), api.get("/brands")]);
        setList(d.data || []);
        setBrands(b.data || []);
    };
    useEffect(() => { reload(); }, []);

    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "download");
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(e => ({ ...e, file_path: data.path, file_name: data.filename }));
        toast.success("File uploaded");
    };

    const save = async () => {
        if (!editing.file_path) return toast.error("Upload a file first");
        try {
            await api.post("/admin/downloads", { ...editing, brand_id: editing.brand_id || null });
            toast.success("Download published");
            setEditing(null); reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this download?")) return;
        await api.delete(`/admin/downloads/${id}`);
        toast.success("Deleted"); reload();
    };

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <div className="flex items-baseline justify-between mb-8">
                    <div><p className="eyebrow mb-2">Resources</p><h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">Download centre</h1></div>
                    <button data-testid="new-download-btn" onClick={() => setEditing({ title: "", category: "ifu", description: "", file_path: "", file_name: "", brand_id: "" })} className="btn-primary"><Plus size={14} strokeWidth={1.5} /> New</button>
                </div>

                <div className="card-base overflow-hidden" data-testid="downloads-table">
                    <table className="w-full text-left">
                        <thead className="bg-zinc-50/60 border-b border-zinc-200">
                            <tr className="text-[10px] uppercase tracking-widest text-zinc-500"><th className="py-3 px-4">Title</th><th className="py-3 px-4">Category</th><th className="py-3 px-4">Brand</th><th className="py-3 px-4">Created</th><th></th></tr>
                        </thead>
                        <tbody>
                            {list.map(d => (
                                <tr key={d.id} className="border-b border-zinc-100">
                                    <td className="py-3 px-4 text-sm font-medium">{d.title}</td>
                                    <td className="py-3 px-4 text-xs uppercase tracking-widest text-zinc-500">{d.category}</td>
                                    <td className="py-3 px-4 text-sm">{brands.find(b => b.id === d.brand_id)?.name || "—"}</td>
                                    <td className="py-3 px-4 text-xs text-zinc-500">{new Date(d.created_at).toLocaleDateString()}</td>
                                    <td className="py-3 px-4 text-right"><button data-testid={`download-delete-${d.id}`} onClick={() => del(d.id)} className="btn-ghost text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button></td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-sm text-zinc-500">No downloads yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setEditing(null)}>
                    <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <p className="eyebrow mb-1">New download</p>
                        <h2 className="font-heading text-2xl mb-6">{editing.title || "Untitled"}</h2>
                        <div className="space-y-4">
                            <div><label className="input-label">Title</label><input data-testid="dl-title" className="input-base" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                            <div>
                                <label className="input-label">Category</label>
                                <select data-testid="dl-category" className="input-base" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="input-label">Brand (optional)</label>
                                <select data-testid="dl-brand" className="input-base" value={editing.brand_id || ""} onChange={(e) => setEditing({ ...editing, brand_id: e.target.value })}>
                                    <option value="">— None —</option>
                                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </div>
                            <div><label className="input-label">Description</label><textarea data-testid="dl-description" className="input-base" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                            <div>
                                <label className="input-label">File</label>
                                <label className="btn-secondary cursor-pointer inline-flex">
                                    <Upload size={14} strokeWidth={1.5} /> {editing.file_name || "Upload PDF or asset"}
                                    <input data-testid="dl-upload" type="file" hidden onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                                </label>
                            </div>
                        </div>
                        <div className="flex gap-3 mt-8 pt-6 border-t border-zinc-200">
                            <button data-testid="dl-save" onClick={save} className="btn-primary">Publish</button>
                            <button onClick={() => setEditing(null)} className="btn-ghost ml-auto">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </Shell>
    );
}
